import { PogiBuyBoxContractError } from '@pogi/buy-box-v2-core';

const OWNER = Symbol.for('pogi.buyBoxV2.formOwner');

const fail = (message) => {
  throw new PogiBuyBoxContractError(message);
};

const controlsForOptionValue = (picker, optionValueId) => Array.from(
  picker.querySelectorAll('input[data-option-value-id], option[data-option-value-id]'),
).filter((control) => control.dataset.optionValueId === String(optionValueId));

/**
 * Capture the native form's authoritative purchase state before an existing
 * Buy Box instance reconnects. Horizon can disconnect/reconnect custom elements
 * during morphs; product controllers must not silently fall back to their
 * Liquid defaults and clear an already selected variant, plan, or quantity.
 */
export function readPogiBuyBoxNativeState(root) {
  if (!(root instanceof HTMLElement) || !root.hasAttribute('data-ready')) return null;
  const section = root.closest('.shopify-section');
  const formId = root.dataset.formId;
  const form = formId ? document.getElementById(formId) : null;
  if (!(section instanceof HTMLElement) || !(form instanceof HTMLFormElement) || !section.contains(form)) {
    return null;
  }
  const variantId = String(form.querySelector('input[name="id"]')?.value || '');
  const sellingPlanId = String(form.querySelector('input[name="selling_plan"]')?.value || '');
  const quantity = Number(form.querySelector('input[name="quantity"]')?.value);
  if (!/^\d+$/.test(variantId)) return null;
  if (sellingPlanId && !/^\d+$/.test(sellingPlanId)) return null;
  return {
    variantId,
    sellingPlanId: sellingPlanId || null,
    quantity: Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null,
  };
}

export function revealPogiBuyBoxNativeControls(root, reason) {
  if (!(root instanceof HTMLElement)) return;
  const section = root.closest('.shopify-section');
  if (!section) return;

  const formId = root.dataset.formId;
  const form = formId ? document.getElementById(formId) : null;
  if (form instanceof HTMLFormElement && section.contains(form)) {
    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput instanceof HTMLInputElement) sellingPlanInput.value = '';
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) delete variantInput.dataset.pogiSync;
  }

  for (const button of root.querySelectorAll('[data-pogi-atc]')) {
    if (button instanceof HTMLButtonElement) button.disabled = true;
  }
  for (const sticky of section.querySelectorAll('pogi-sticky-atc')) {
    sticky.classList.remove('is-visible');
    sticky.setAttribute('inert', '');
    sticky.setAttribute('aria-hidden', 'true');
    const button = sticky.querySelector('.pogi-satc__btn');
    if (button instanceof HTMLButtonElement) button.disabled = true;
  }

  section.setAttribute('data-pogi-buy-box-fallback', '');
  root.removeAttribute('data-ready');
  root.setAttribute('inert', '');
  root.setAttribute('aria-busy', 'false');
  root.dataset.pogiV2Failure = String(reason?.message || reason || 'initialization failed');
  for (const nativeRoot of section.querySelectorAll(
    'variant-picker[ref="mainVariantPicker"], .buy-buttons-block, .buy-buttons-block [ref="productFormButtons"]',
  )) nativeRoot.removeAttribute('inert');
}

export class PogiBuyBoxHorizonAdapter {
  constructor(root, model) {
    if (!(root instanceof HTMLElement)) fail('Horizon adapter root must be an HTMLElement');
    this.root = root;
    this.model = model;
    this.contract = model.contract;
    this.section = root.closest('.shopify-section');
    if (!this.section) fail('Buy Box must be inside one Shopify section');

    const matchingBuyBoxes = Array.from(this.section.querySelectorAll('.pogi-buy-box[data-form-id]'))
      .filter((buyBox) => buyBox.dataset.formId === this.contract.instance.formId && !buyBox.hidden);
    if (matchingBuyBoxes.length !== 1 || matchingBuyBoxes[0] !== root) {
      fail(`Expected one Buy Box owner for form ${this.contract.instance.formId}`);
    }

    this.form = document.getElementById(this.contract.instance.formId);
    if (!(this.form instanceof HTMLFormElement)) fail(`Product form ${this.contract.instance.formId} is missing`);
    this.productForm = this.form.closest('product-form-component');
    if (!(this.productForm instanceof HTMLElement)) fail('Product form is not owned by product-form-component');
    if (
      this.productForm.dataset.productId
      && this.productForm.dataset.productId !== this.contract.instance.productId
    ) fail('Buy Box product does not match product-form-component');

    const pickers = Array.from(this.section.querySelectorAll('variant-picker'));
    this.picker = pickers.find((picker) => picker.dataset.productId === this.contract.instance.productId)
      ?? (pickers.length === 1 ? pickers[0] : null);
    if (!(this.picker instanceof HTMLElement)) fail('Matching Horizon variant-picker is missing or ambiguous');
    this.cartQuantities = new Map(
      this.contract.variants.map((variant) => [variant.id, variant.quantityRule.cartQuantity]),
    );
    this.claimed = false;
  }

  claim() {
    const owner = this.form[OWNER];
    if (owner && owner !== this.root) fail(`Product form ${this.form.id} already has a Buy Box owner`);
    this.form[OWNER] = this.root;
    this.claimed = true;
    this.mediaEvents?.abort();
    this.mediaEvents = new AbortController();
    const prepare = () => this.#prepareGalleryMedia();
    this.root.addEventListener('pogi:buybox-ready', prepare, { signal: this.mediaEvents.signal });
    this.section.addEventListener('pogi:gallery-ready', prepare, { signal: this.mediaEvents.signal });
    if (this.root.hasAttribute('data-ready')) queueMicrotask(prepare);
  }

  release() {
    if (this.claimed && this.form[OWNER] === this.root) delete this.form[OWNER];
    this.claimed = false;
    this.pendingNativeChange = null;
    this.mediaEvents?.abort();
  }

  #galleryMediaContext(variant) {
    const seen = new Set([variant.mediaId]);
    const alternatives = this.contract.variants
      .filter((candidate) => candidate.available && candidate.id !== variant.id)
      .map((candidate) => ({
        candidate,
        changedAxes: this.contract.axes.filter((axis) => candidate.selections[axis.key] !== variant.selections[axis.key]).map((axis) => axis.key),
      }))
      .sort((left, right) => left.changedAxes.length - right.changedAxes.length)
      .filter(({ candidate }) => {
        if (seen.has(candidate.mediaId)) return false;
        seen.add(candidate.mediaId);
        return true;
      });
    // Cover another kind of likely choice before warming two dimension/scent
    // alternatives and leaving the first pack-count change entirely cold.
    const first = alternatives[0];
    const second = first && (alternatives.slice(1).find(({ changedAxes }) =>
      changedAxes.some((axis) => !first.changedAxes.includes(axis))) || alternatives[1]);
    const alternativeMediaIds = [first, second].filter(Boolean).map(({ candidate }) => candidate.mediaId);
    return { productId: this.contract.instance.productId, variantId: variant.id, mediaId: variant.mediaId, alternativeMediaIds };
  }

  #prepareGalleryMedia() {
    if (!this.claimed || this.form[OWNER] !== this.root || !this.root.isConnected || !this.root.hasAttribute('data-ready')) return;
    const variant = this.model.variantById(this.form.querySelector('input[name="id"]')?.value);
    if (!variant) return;
    this.#updateGalleryMedia('prepareVariantMedia', variant);
  }

  #updateGalleryMedia(method, variant) {
    // A photo optimization must never prevent a validated commerce selection.
    try {
      const gallery = this.section.querySelector('media-gallery');
      gallery?.[method]?.(this.#galleryMediaContext(variant));
    } catch (error) {
      console.warn('[pogi-variant-media] Photo preparation unavailable:', error);
    }
  }

  /**
   * Refresh cart-owned quantity state from a resolved standard cart event. The
   * event carries the authoritative full Ajax cart, so property- or plan-split
   * lines for the same variant are summed before enforcing quantity-rule max.
   */
  updateCartItems(items) {
    if (!Array.isArray(items)) return false;
    const next = new Map(this.contract.variants.map((variant) => [variant.id, 0]));
    for (const item of items) {
      const variantId = String(item?.variant_id ?? item?.variantId ?? item?.id ?? '');
      const quantity = Number(item?.quantity);
      if (!/^\d+$/.test(variantId) || !Number.isSafeInteger(quantity) || quantity < 0) return false;
      if (next.has(variantId)) {
        const total = next.get(variantId) + quantity;
        if (!Number.isSafeInteger(total)) return false;
        next.set(variantId, total);
      }
    }
    this.cartQuantities = next;
    return true;
  }

  updateCurrentCartQuantityFromForm(changedInput = null) {
    const variantId = String(this.form.querySelector('input[name="id"]')?.value ?? '');
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const quantityComponent = quantityInput?.closest('quantity-selector-component');
    const cartQuantityValue = quantityInput?.getAttribute('data-cart-quantity');
    const cartQuantity = Number(cartQuantityValue);
    if (
      (changedInput !== null && changedInput !== quantityInput)
      || !(quantityComponent instanceof HTMLElement)
      || quantityComponent.dataset.variantId !== variantId
      || cartQuantityValue === null
      || !this.cartQuantities.has(variantId)
      || !Number.isSafeInteger(cartQuantity)
      || cartQuantity < 0
    ) return false;
    this.cartQuantities.set(variantId, cartQuantity);
    return true;
  }

  quantityState(variantId, requestedQuantity) {
    const normalizedVariantId = String(variantId ?? '');
    return this.model.quantityState(
      normalizedVariantId,
      requestedQuantity,
      this.cartQuantities.get(normalizedVariantId) ?? null,
    );
  }

  stepQuantity(variantId, currentQuantity, direction) {
    const normalizedVariantId = String(variantId ?? '');
    return this.model.stepQuantity(
      normalizedVariantId,
      currentQuantity,
      direction,
      this.cartQuantities.get(normalizedVariantId) ?? null,
    );
  }

  syncPurchaseInputs({ variantId, sellingPlanId = null, quantity }) {
    const variant = this.model.variantById(variantId);
    if (!variant) fail(`Cannot synchronize unknown variant ${variantId}`);
    this.model.assertPlanAllocation(variant.id, sellingPlanId);
    const quantityState = this.quantityState(variant.id, quantity);

    const idInput = this.form.querySelector('input[name="id"]');
    if (!(idInput instanceof HTMLInputElement)) fail('Product form variant input is missing');
    idInput.value = variant.id;
    idInput.dataset.pogiSync = '1';

    let planInput = this.form.querySelector('input[name="selling_plan"]');
    if (!(planInput instanceof HTMLInputElement)) {
      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      this.form.appendChild(planInput);
    }
    planInput.value = sellingPlanId == null ? '' : String(sellingPlanId);

    const quantityComponent = this.form.querySelector('quantity-selector-component');
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    if (quantityComponent && typeof quantityComponent.setValue === 'function') {
      quantityComponent.setValue(String(quantityState.value));
      quantityComponent.updateButtonStates?.();
    } else if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(quantityState.value);
    }
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Localization and reload use this URL to restore the purchase selection.
    // Keep it paired with the validated form, including an explicit one-time choice.
    if (
      this.picker.dataset.templateProductMatch === 'true'
      && !this.picker.closest('product-card, quick-add-dialog')
    ) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      url.searchParams.delete('option_values');
      if (sellingPlanId == null) url.searchParams.delete('selling_plan');
      else url.searchParams.set('selling_plan', String(sellingPlanId));
      if (url.href !== window.location.href) history.replaceState(history.state, '', url.toString());
    }
    return quantityState;
  }

  /**
   * Set every native option control first, then emit exactly one bubbling change.
   * Horizon reads all checked option-value IDs from that single event and owns
   * the ProductSelectEvent promise, section request, picker morph, and form queue.
   */
  selectVariant(variantId) {
    const variant = this.model.variantById(variantId);
    if (!variant) fail(`Cannot select unknown variant ${variantId}`);

    const idInput = this.form.querySelector('input[name="id"]');
    if (!(idInput instanceof HTMLInputElement)) fail('Product form variant input is missing');

    const changedControls = [];
    for (const axis of this.contract.axes) {
      const selectedValue = axis.values.find(
        (value) => value.shopifyValue === variant.selections[axis.key],
      );
      if (!selectedValue) fail(`Variant ${variant.id} has no ${axis.key} option value`);
      const controls = controlsForOptionValue(this.picker, selectedValue.optionValueId);
      if (controls.length === 0) {
        if (axis.values.length === 1) continue;
        fail(`Native picker is missing option value ${selectedValue.optionValueId}`);
      }
      if (controls.length !== 1) fail(`Native picker duplicated option value ${selectedValue.optionValueId}`);
      const control = controls[0];
      if (control instanceof HTMLInputElement) {
        if (!control.checked) changedControls.push(control);
        control.checked = true;
      } else if (control instanceof HTMLOptionElement) {
        const select = control.closest('select');
        if (!(select instanceof HTMLSelectElement)) fail('Native picker option has no select owner');
        if (!control.selected) changedControls.push(select);
        for (const option of select.options) option.removeAttribute('selected');
        control.selected = true;
        control.setAttribute('selected', 'selected');
      }
    }

    const dispatchTarget = changedControls.at(-1);
    if (dispatchTarget) {
      const selectedOption = dispatchTarget instanceof HTMLSelectElement
        ? dispatchTarget.options[dispatchTarget.selectedIndex]
        : dispatchTarget;
      if (!(selectedOption instanceof HTMLElement)) fail('Native picker change target has no selected option');
      // Horizon derives the canonical ?variant= value and ProductSelectEvent detail
      // from the one control that receives the change event. A product-specific UI
      // can change multiple axes atomically, so that control's server-rendered
      // data-variant-id may describe the former combination. Pair the single native
      // event with the already exact normalized result before Horizon consumes it.
      selectedOption.dataset.variantId = variant.id;
    }

    // Do not arm the form until every native option control has been validated.
    idInput.value = variant.id;
    idInput.dataset.pogiSync = '1';
    if (dispatchTarget) {
      const receivers = [this.picker, this.productForm, this.section.querySelector('media-gallery')]
        .filter((element) => element instanceof HTMLElement);
      if (receivers.every((element) => element.matches(':defined'))) {
        this.pendingNativeChange = null;
        this.#updateGalleryMedia('previewVariantMedia', variant);
        dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Custom controls can become usable before Horizon upgrades its picker,
        // form and gallery. Preserve the latest intent until their listeners
        // exist; emitting early silently loses the section/gallery update.
        const pending = { target: dispatchTarget, variantId: variant.id };
        this.pendingNativeChange = pending;
        Promise.all(receivers.map((element) => customElements.whenDefined(element.localName))).then(() => {
          if (this.pendingNativeChange !== pending) return;
          this.pendingNativeChange = null;
          if (
            !this.claimed || this.form[OWNER] !== this.root
            || !this.root.isConnected || !this.picker.isConnected || !this.form.isConnected
            || !this.picker.contains(pending.target) || !this.form.contains(idInput)
            || idInput.value !== pending.variantId
            || this.section.hasAttribute('data-pogi-buy-box-fallback')
          ) return;
          this.#updateGalleryMedia('previewVariantMedia', variant);
          pending.target.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    }
    return { variant, dispatched: Boolean(dispatchTarget) };
  }

  revealNativeControls(reason) {
    this.release();
    revealPogiBuyBoxNativeControls(this.root, reason);
  }
}
